<template>
  <div v-if="isAssessor || isOwner">
    <b-row class="justify-content-md-center m-0">
      <b-col lg="6">
        <div class="h5 display-4 mt-1 mb-4 font-weight-bold">Evaluate</div>
        <div class="h5 font-weight-bold font-size-lg">My micro-credentials</div>

        <search-bar
          v-model="searchKeyWords"
          class="mt-4"
          placeholder="Search for micro-credentials"
          @reset="onSearchReset"
        ></search-bar>
        <BaseLists
          :items="learningUnits"
          :count-lazy-loading="countLazyLoading"
          :is-loading="isLoading"
          :no-more-item="noMoreItem"
          :toggle-fetching="toggleFetching"
          :show-detail="false"
          class="card__mc"
          @clickItem="goDesignAndPlanningInfoPage($event)"
          @fetchNewItems="fetchMore"
        >
          <template v-slot:thumbnail="learningUnit">
            <mc-explore-thumbnail
              :key="`thumbnail--${learningUnit.item.id}`"
              :learning-unit="learningUnit.item"
              class="img-size"
            />
          </template>
          <template v-slot:list-options="learningUnit">
            <div class="ml-u6">
              <div class="font-size-lg text__mc-title text__mc-title">
                {{ learningUnit.item.name }}
              </div>
              <div v-if="learningUnit.item.level_type" class="text-primary font-weight-bold my-3">
                <span class="mr-1"
                  ><img
                    :src="
                      learningUnit.item.level_type === 'CAREER_SPECIFIC'
                        ? require('@/assets/learning-unit-types/specific.svg')
                        : require('@/assets/learning-unit-types/academic.svg')
                    "
                    style="height: 16px"
                  />
                </span>
                <span class="text__mc-type">{{
                  learningUnit.item.level_type === 'CAREER_SPECIFIC'
                    ? 'Career Specific'
                    : 'Academic Focused'
                }}</span>
              </div>
              <div class="d-flex text-description font-size-sm m-auto">
                <div class="mr-u4">PENDING ASSESSMENTS</div>
                <div class="mr-u4">|</div>
                <div class="text__learners">
                  {{ learningUnit.item.inprogress_by_assessor_count }}
                </div>
              </div>
            </div>
          </template>
        </BaseLists>
        <div
          v-if="!hasData && !isLoading"
          ref="container__empty-search-result"
          class="text-dark mr-auto px-2"
        >
          <div class="my-4">
            Sorry, we couldn't find any results
            <span class="d-block">Try adjusting your search, Here are some ideas:</span>
          </div>
          <ul>
            <li>Make sure all words are spelled correctly</li>
            <li>Try different search terms</li>
            <li>Try more general search terms</li>
          </ul>
        </div>
        <div v-if="isStateChanging" key="loading" class="d-flex justify-content-center p-4 m-3">
          <b-spinner label="Loading..." variant="dark" />
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import LearningUnit from '@/models/LearningUnit';
import { BaseLists } from '@/components/base-components';
import SearchBar from '@/views/LearningUnit/components/SearchBar.vue';

import McExploreThumbnail from '../LearningUnit/components/McExploreThumbnail.vue';
import { userInfoMixin } from '@/mixins/userInfoMixin';

export default {
  components: {
    BaseLists,
    McExploreThumbnail,
    SearchBar
  },

  mixins: [userInfoMixin],

  data() {
    return {
      isLoading: true,
      learningUnitIds: [],
      countLazyLoading: 0,
      noMoreItem: false,
      toggleFetching: true,
      searchKeyWords: [],
      hasData: false,
      isStateChanging: false
    };
  },
  computed: {
    learningUnits() {
      return this.learningUnitIds.map((id) => LearningUnit.find(id)).filter(Boolean);
    },

    isAssessor() {
      return this.user.isAssessor();
    },
    isOwner() {
      return this.user.isOwner();
    },
    fetchParams() {
      const filter = {};

      if (this.searchKeyWords.length) this.$set(filter, 'name', this.searchKeyWords);

      return filter;
    }
  },
  watch: {
    searchKeyWords: {
      async handler() {
        this.isLoading = true;
        this.isStateChanging = true;
        await this.search();
      },
      deep: true
    }
  },

  async created() {
    this.isStateChanging = true;
    await this.fetchInitial();
  },

  methods: {
    async fetchInitial() {
      if (!this.isAssessor && !this.isOwner) this.$router.push({ path: '/not-found' });

      this.learningUnitIds = [];

      try {
        this.isLoading = true;
        this.noMoreItem = false;

        const { response } = await LearningUnit.api().fetchByAssessor(this.fetchParams, 0, 15);

        if (response.data.length) {
          this.learningUnitIds.push(...response.data.map(({ id }) => id));

          this.countLazyLoading++;
        } else {
          this.noMoreItem = true;
        }
      } catch (error) {
        this.noMoreItem = true;

        this.$toast.error('Cannot fetch data, please try again');
      } finally {
        this.hasData = this.learningUnitIds.length > 0;
        this.isLoading = false;
        this.isStateChanging = false;
      }

      this.isLoading = false;
    },

    async fetchMore(start, limit) {
      try {
        this.toggleFetching = true;

        const { response } = await LearningUnit.api().fetchByAssessor(
          this.fetchParams,
          start,
          limit
        );

        if (response.data.length) {
          this.learningUnitIds.push(...response.data.map(({ id }) => id));

          this.countLazyLoading++;
        } else {
          this.noMoreItem = true;
        }
      } catch (error) {
        this.noMoreItem = true;

        this.$toast.error('Cannot fetch data, please try again');
      }

      this.toggleFetching = false;
    },

    async search() {
      try {
        this.isLoading = true;
        this.noMoreItem = false;
        this.learningUnitIds = [];
        await this.fetchInitial();
      } catch (error) {
        this.$toast('Cannot load data, please try again');
      }
    },

    goDesignAndPlanningInfoPage(learningUnit) {
      this.$router.push({
        name: 'design-planning/info',
        params: {
          learningUnitId: learningUnit.id
        },
        query: {
          tab: 'assess',
          mode: 'assessor'
        }
      });
    },
    onSearchReset() {
      this.searchKeyWords = [];
    }
  }
};
</script>

<style scoped>
.img-size {
  max-width: 160px;
  height: 100px;
  max-height: max-content;
}
</style>
